import React, { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Ruler, Scissors, Shapes } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { SHAPE_MAP, buildShape, fmt } from "../lib/sewingShapes";
import { buildCompositeById, JOINT_MAP } from "../lib/sewingComposites";
import { COMPOSITES } from "../lib/sewingCurriculum";
import { labHref } from "../lib/sewingLinks";
import FoldViewer from "../components/sewing/FoldViewer";
import PatternPieces from "../components/sewing/PatternPieces";

/* ============================================================
   /sewing-primitives/make/:compositeId — one page per starter-
   menu object. The 3D model assembles from its primitive parts,
   the size dial regenerates every pattern measurement (the
   first taste of the creature generator), and the cut list
   shows exactly what you'd draw on fabric for each part.
   ============================================================ */

const SewingCompositePage = () => {
  const { compositeId } = useParams();
  const meta = COMPOSITES.find((c) => c.id === compositeId);
  const [size, setSize] = useState(1);
  const model = useMemo(
    () => (meta ? buildCompositeById(compositeId, size) : null),
    [meta, compositeId, size]
  );

  // Group identical parts (e.g. two arms) into one cut-list entry.
  const groups = useMemo(() => {
    if (!model) return [];
    const byKey = new Map();
    model.parts.forEach((part) => {
      const key = `${part.name}|${part.shape}|${JSON.stringify(part.params)}`;
      if (byKey.has(key)) byKey.get(key).count += 1;
      else byKey.set(key, { ...part, count: 1 });
    });
    return [...byKey.values()];
  }, [model]);

  if (!meta) return <Navigate to="/sewing-primitives" replace />;

  const jointsUsed = [...new Set(model.attachments.map((a) => a.joint))].map(
    (id) => JOINT_MAP[id]
  );
  const standsTall = Math.max(...model.positionsAt(1).map((p) => p[1]));

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-aurora"
      />
      <div className="container relative mx-auto max-w-5xl px-4 py-14">
        <Link
          to="/sewing-primitives/make"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          back to make
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-4xl" aria-hidden>
            {meta.emoji}
          </span>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            <span className="text-gold-shimmer">Plush {meta.name.toLowerCase()}</span>
          </h1>
          <span className="text-sm text-primary" title={`difficulty ${meta.difficulty}/3`}>
            {"✂".repeat(meta.difficulty)}
          </span>
        </div>
        <p className="mt-3 max-w-2xl text-muted-foreground">{meta.plan}</p>

        <div className="mt-8 grid gap-5 lg:grid-cols-[1.2fr,1fr]">
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {meta.name} — parts ↔ assembled
              </CardTitle>
              <CardDescription>{model.insight}</CardDescription>
            </CardHeader>
            <CardContent>
              <FoldViewer
                model={model}
                labels={{
                  start: "apart",
                  end: "assembled",
                  button: "put it together",
                  aria: `Assemble the ${meta.name.toLowerCase()} from its parts`,
                  sceneAria: `3D view of the plush ${meta.name.toLowerCase()} assembling from primitive parts`,
                }}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-5">
            <Card className="bg-card/60 backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Ruler className="h-4 w-4 text-primary" />
                  Size dial
                </CardTitle>
                <CardDescription>
                  One slider, every measurement recomputed — patterns are math,
                  so resizing a plush is free.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <label className="block text-sm">
                  <span className="flex items-baseline justify-between">
                    <span className="font-medium">scale</span>
                    <span className="tabular-nums text-muted-foreground">
                      ×{size.toFixed(2)} · stands ~{fmt(standsTall)} cm tall
                    </span>
                  </span>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.05"
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className="mt-1 w-full accent-primary"
                    aria-label="Scale the whole plush"
                  />
                </label>
              </CardContent>
            </Card>

            <Card className="bg-card/60 backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Joints used here</CardTitle>
                <CardDescription>
                  Dot colors in the 3D view — where and how parts attach.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {jointsUsed.map((joint) => (
                  <div key={joint.id} className="flex items-start gap-2.5 text-sm">
                    <span
                      aria-hidden
                      className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border border-white/60"
                      style={{ backgroundColor: joint.color }}
                    />
                    <div>
                      <p className="font-semibold">{joint.name}</p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        {model.attachments
                          .filter((a) => a.joint === joint.id)
                          .map((a) => a.label)
                          .join(" · ")}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Cut list: the drafting table for every distinct part. */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Scissors className="h-6 w-6 text-primary" />
            What you'd cut
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Every part is a lab primitive, so its pattern is generated from the
            measurements above — move the size dial and watch the numbers
            follow. Seam allowance is not included; add ~0.7 cm everywhere.
          </p>
          <div className="mt-6 space-y-8">
            {groups.map((group) => (
              <div key={`${group.name}-${group.shape}-${group.id}`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold capitalize">{group.name}</h3>
                  <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-primary">
                    make {group.count}
                  </span>
                  <Link
                    to={labHref(group.shape, group.params)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    <Shapes className="h-3.5 w-3.5" />
                    open {SHAPE_MAP[group.shape].name.toLowerCase()} in the lab
                  </Link>
                  {group.note && (
                    <span className="text-xs text-muted-foreground">— {group.note}</span>
                  )}
                </div>
                <PatternPieces model={buildShape(group.shape, group.params)} />
              </div>
            ))}
          </div>
        </section>

        <p className="mt-14 text-center text-sm text-muted-foreground">
          Pick a different one from the{" "}
          <Link to="/sewing-primitives/make" className="text-primary hover:underline">
            starter menu
          </Link>
          . 🧵
        </p>
      </div>
    </div>
  );
};

export default SewingCompositePage;
